import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { omit, reject } from 'lodash-es'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useNotification } from '@editorHooks'
import { getProjectFile, getProjectFiles } from '@queries'
import { generateTemplatePreview } from '@mutations'
import { retryUntilSuccessOrFailure } from 'src/state/utils'

const useGenerateTemplateFiles = (rootGuid: string) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { triggerNotification } = useNotification()

  const [loadedTemplates, setLoadedTemplates] = useState<
    Record<string, 'loading' | 'loaded' | 'error'>
  >({})

  /**
   * Generates the preview of one template file and stores it in the files query. Tracks the
   * file in `loadedTemplates` (without touching other files) and rethrows failures.
   */
  const generateTemplate = useCallback(
    async (fileGuid: string) => {
      setLoadedTemplates(prev => ({ ...prev, [fileGuid]: 'loading' }))
      try {
        const { task_id } = await generateTemplatePreview.request(
          projectId,
          rootGuid,
          fileGuid,
          true,
        )

        await retryUntilSuccessOrFailure({
          url: `/task-status/${task_id}`,
        })

        const file = await getProjectFile.request(projectId, rootGuid, fileGuid)

        const filesKey = getProjectFiles.getKey(projectId, rootGuid)

        queryClient.setQueryData<FilesResult>(filesKey, prev => {
          if (!prev) return undefined

          const newFiles = reject(prev.files, { guid: file.guid })

          return {
            ...prev,
            files: [...newFiles, file],
          }
        })
      } finally {
        setLoadedTemplates(prev => omit(prev, fileGuid))
      }
    },
    [projectId, rootGuid, queryClient],
  )

  const { mutateAsync: generateTemplates, isLoading: isGenerating } = useMutation({
    mutationFn: async (fileGuids: string[]) => {
      const processGuid = async (fileGuid: string) => {
        try {
          await generateTemplate(fileGuid)
        } catch (error) {
          // TODO: set and display as error
          enqueueSnackbar(
            buildErrorMessage(error as AxiosError, `Fehler beim Generieren der Datei ${fileGuid}`),
            {
              variant: 'error',
              persist: true,
            },
          )
        }
      }

      // Process files in batches of 10
      const batchSize = 20
      for (let i = 0; i < fileGuids.length; i += batchSize) {
        const batch = fileGuids.slice(i, i + batchSize)
        await Promise.all(batch.map(processGuid))
      }

      return fileGuids
    },
    onSuccess: () => {
      triggerNotification({ message: 'Vorschau der Templates erfolgreich generiert ✅' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Generieren'), {
        variant: 'error',
        persist: true,
      })
    },
  })

  return { generateTemplates, generateTemplate, isGenerating, loadedTemplates }
}

export default useGenerateTemplateFiles
